import React, { memo, useEffect, useState } from 'react';
import {
  Image,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  useWindowDimensions,
  View,
} from 'react-native';
import Svg, {
  Circle,
  Defs,
  G,
  Line,
  LinearGradient,
  Path,
  RadialGradient,
  Stop,
} from 'react-native-svg';

import { COLOR, FONT, FONT_SIZE } from '../../../constants/styles';

const NAVY = COLOR.labelText;
const BLUE = COLOR.button;
const CORAL = COLOR.emphasis;
const CORAL_DARK = COLOR.error;
const MUTED = COLOR.textLight;
const STACKED_BREAKPOINT = 860;
const MOBILE_BREAKPOINT = 560;
const VERTICAL_GRID_LINES = Array.from({ length: 15 }, (_, index) => (index + 1) * 30);
const HORIZONTAL_GRID_LINES = Array.from({ length: 17 }, (_, index) => (index + 1) * 30);

const Grid = memo(() => (
  <Svg style={StyleSheet.absoluteFill} viewBox="0 0 480 540" preserveAspectRatio="none">
    {VERTICAL_GRID_LINES.map((position) => (
      <Line
        key={`vertical-${position}`}
        x1={position}
        y1="0"
        x2={position}
        y2="540"
        stroke={BLUE}
        strokeOpacity="0.08"
      />
    ))}
    {HORIZONTAL_GRID_LINES.map((position) => (
      <Line
        key={`horizontal-${position}`}
        x1="0"
        y1={position}
        x2="480"
        y2={position}
        stroke={BLUE}
        strokeOpacity="0.08"
      />
    ))}
  </Svg>
));

const BrokenLinkIllustration = memo(() => (
  <Svg width="100%" height="100%" viewBox="0 0 360 360">
    <Defs>
      <LinearGradient id="link-expired-core" x1="110" y1="95" x2="255" y2="265">
        <Stop offset="0" stopColor={BLUE} />
        <Stop offset="1" stopColor={NAVY} />
      </LinearGradient>
      <RadialGradient id="link-expired-inner-shadow" cx="42%" cy="37%" r="68%">
        <Stop offset="48%" stopColor={COLOR.darkest} stopOpacity="0" />
        <Stop offset="68%" stopColor="#08101f" stopOpacity="0.12" />
        <Stop offset="90%" stopColor="#030812" stopOpacity="0.50" />
      </RadialGradient>
    </Defs>
    <Circle cx="180" cy="180" r="180" fill={COLOR.contrast} fillOpacity="0.48" />
    <Circle
      cx="180"
      cy="180"
      r="166"
      fill="none"
      stroke={BLUE}
      strokeOpacity="0.08"
      strokeWidth="26"
    />
    <Circle cx="180" cy="180" r="164" fill="none" stroke={CORAL} strokeWidth="4" />
    <Circle cx="180" cy="180" r="142" fill="none" stroke={CORAL} strokeWidth="11" />
    <Circle cx="180" cy="180" r="126" fill="url(#link-expired-core)" />
    <Circle cx="180" cy="180" r="126" fill="url(#link-expired-inner-shadow)" />

    <G transform="translate(91 91) scale(5.1)">
      <Path
        d="M21.19 13.51c-2.91-2.9-7.82-2.79-10.96.35l-7.78 7.78c-3.15 3.15-3.26 8.06-.35 10.96s7.81 2.79 10.96-.35l3.89-3.89a1.5 1.5 0 0 0-2.12-2.12l-3.89 3.89c-1.93 1.93-4.94 2.13-6.72.35-1.78-1.78-1.58-4.79.35-6.72l7.78-7.78c1.93-1.93 4.94-2.13 6.72-.35a1.5 1.5 0 1 0 2.12-2.12Zm-7.78 7.78c2.9 2.9 7.81 2.79 10.96-.35l7.78-7.78c3.15-3.15 3.26-8.06.35-10.96-2.9-2.9-7.81-2.79-10.96.35l-3.89 3.89a1.5 1.5 0 1 0 2.12 2.12l3.89-3.89c1.93-1.93 4.94-2.13 6.72-.35 1.78 1.78 1.58 4.79-.35 6.72l-7.78 7.78c-1.93 1.93-4.94 2.13-6.72.35a1.5 1.5 0 1 0-2.12 2.12Z"
        fill={COLOR.contrast}
      />
      <Path
        d="M20.39 28.91v3a1.5 1.5 0 0 0 3 0v-3a1.5 1.5 0 0 0-3 0Zm4.33-2.03 2.12 2.12a1.5 1.5 0 1 0 2.12-2.12l-2.12-2.13a1.5 1.5 0 0 0-2.12 2.13Zm4.16-3.45h3a1.5 1.5 0 0 0 0-3h-3a1.5 1.5 0 0 0 0 3ZM5.72 11.23h-3a1.5 1.5 0 0 0 0 3h3a1.5 1.5 0 0 0 0-3ZM9.87 7.78 7.75 5.66a1.5 1.5 0 1 0-2.12 2.12l2.12 2.12a1.5 1.5 0 0 0 2.12-2.12ZM14.2 5.75v-3a1.5 1.5 0 0 0-3 0v3a1.5 1.5 0 0 0 3 0Z"
        fill={CORAL}
      />
    </G>
  </Svg>
));

const LinkExpired = () => {
  const { width } = useWindowDimensions();
  const [isButtonHovered, setIsButtonHovered] = useState(false);
  const isStacked = width <= STACKED_BREAKPOINT;
  const isMobile = width <= MOBILE_BREAKPOINT;

  useEffect(() => {
    document.title = 'This link has expired | Outpatient App';
  }, []);

  const handleSignIn = () => {
    window.location.assign('/');
  };

  return (
    <View style={styles.page}>
      <View style={[styles.backgroundGlow, styles.backgroundGlowTop]} />
      <View style={[styles.backgroundGlow, styles.backgroundGlowBottom]} />
      <View style={[styles.orbit, styles.topOrbit]} />
      <View style={[styles.orbit, styles.bottomOrbit]} />

      <ScrollView
        contentContainerStyle={[styles.scrollContent, isMobile && styles.scrollContentMobile]}
        style={styles.scrollView}
      >
        <View style={styles.header}>
          <View style={styles.logoWrap}>
            <Image
              style={styles.brandingImage}
              source={require('../../../../resources/icon.png')}
            />
            <Text style={styles.brandingText}>OUTPATIENT APP</Text>
          </View>
          <View style={styles.status}>
            <View style={styles.statusDot} />
            {!isMobile && <Text style={styles.statusText}>SECURE LINK</Text>}
          </View>
        </View>

        <View style={[styles.main, isStacked && styles.mainStacked]}>
          <View
            style={[styles.card, isStacked && styles.cardStacked, isMobile && styles.cardMobile]}
          >
            <View
              style={[
                styles.artPanel,
                isStacked && styles.artPanelStacked,
                isMobile && styles.artPanelMobile,
              ]}
            >
              <Grid />
              <View style={styles.panelOrbit} />
              <View
                style={[
                  styles.illustration,
                  isStacked && styles.illustrationStacked,
                  isMobile && styles.illustrationMobile,
                ]}
              >
                <BrokenLinkIllustration />
              </View>
            </View>

            <View
              style={[
                styles.contentPanel,
                isStacked && styles.contentPanelStacked,
                isMobile && styles.contentPanelMobile,
              ]}
            >
              <View style={styles.contentInner}>
                <View style={styles.eyebrow}>
                  <Text style={styles.eyebrowText}>LINK EXPIRED</Text>
                </View>
                <Text
                  accessibilityRole="header"
                  style={[styles.title, isMobile && styles.titleMobile]}
                >
                  This link has expired.
                </Text>
                <Text style={[styles.message, isMobile && styles.messageMobile]}>
                  For your security, invitation and password reset links are only active for a
                  limited time.
                </Text>

                <Pressable
                  accessibilityRole="button"
                  onHoverIn={() => setIsButtonHovered(true)}
                  onHoverOut={() => setIsButtonHovered(false)}
                  onPress={handleSignIn}
                  style={[
                    styles.button,
                    isButtonHovered && styles.buttonHovered,
                    isMobile && styles.buttonMobile,
                  ]}
                >
                  <Text style={styles.buttonText}>Return to sign in</Text>
                  <Text style={styles.buttonArrow}>→</Text>
                </Pressable>

                <View style={styles.helpNote}>
                  <View style={styles.infoIcon}>
                    <Text style={styles.infoIconText}>i</Text>
                  </View>
                  <Text style={styles.helpText}>
                    If you continue to have trouble, contact the organization that sent you the
                    original link.
                  </Text>
                </View>
              </View>
            </View>
          </View>
        </View>
      </ScrollView>
    </View>
  );
};

export default memo(LinkExpired);

const styles = StyleSheet.create({
  page: {
    flex: 1,
    width: '100%',
    height: '100%',
    minHeight: '100vh',
    backgroundColor: NAVY,
    overflow: 'hidden',
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
    minHeight: '100vh',
    paddingTop: 20,
    paddingLeft: 40,
    paddingRight: 48,
    paddingBottom: 10,
  },
  scrollContentMobile: {
    paddingTop: 20,
    paddingLeft: 14,
    paddingRight: 14,
    paddingBottom: 18,
  },
  backgroundGlow: {
    position: 'absolute',
    width: 650,
    height: 650,
    borderRadius: 325,
  },
  backgroundGlowTop: {
    top: -360,
    left: -360,
    backgroundImage: `radial-gradient(circle, ${COLOR.emphasis_rgba_10} 0%, ${COLOR.emphasis_rgba_0} 72%)`,
  },
  backgroundGlowBottom: {
    right: -360,
    bottom: -390,
    backgroundImage: `radial-gradient(circle, ${BLUE}33 0%, ${BLUE}00 72%)`,
  },
  orbit: {
    position: 'absolute',
    width: 304,
    height: 304,
    borderWidth: 1,
    borderColor: `${COLOR.contrast}12`,
    borderRadius: 152,
  },
  topOrbit: {
    top: -185,
    right: -10,
  },
  bottomOrbit: {
    left: '8%',
    bottom: -208,
  },
  header: {
    width: '100%',
    maxWidth: 1120,
    minHeight: 50,
    marginHorizontal: 'auto',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  status: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 9,
  },
  statusDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: CORAL,
    boxShadow: `0 0 0 5px ${COLOR.emphasis_rgba_10}`,
  },
  statusText: {
    ...FONT.bold,
    color: COLOR.textReversed,
    fontSize: FONT_SIZE.tiny,
    letterSpacing: 1.7,
    opacity: 0.8,
  },
  main: {
    flexGrow: 1,
    flexShrink: 0,
    alignItems: 'center',
    justifyContent: 'center',
  },
  mainStacked: {
    paddingTop: 28,
    paddingBottom: 10,
  },
  card: {
    width: '100%',
    maxWidth: 1120,
    height: 550,
    flexDirection: 'row',
    borderWidth: 1,
    borderColor: `${COLOR.contrast}38`,
    borderRadius: 24,
    backgroundColor: COLOR.contrast,
    boxShadow: `0 34px 90px ${COLOR.overlay}`,
    overflow: 'hidden',
  },
  cardStacked: {
    width: '100%',
    maxWidth: 640,
    height: 'auto',
    flexDirection: 'column',
  },
  cardMobile: {
    minHeight: 0,
    borderRadius: 22,
  },
  artPanel: {
    position: 'relative',
    flexBasis: '46%',
    flexGrow: 0,
    flexShrink: 0,
    minWidth: 0,
    minHeight: 0,
    padding: 30,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: COLOR.altContrast,
    overflow: 'hidden',
  },
  artPanelStacked: {
    flexBasis: 'auto',
    minHeight: 285,
    paddingTop: 28,
    paddingHorizontal: 56,
    paddingBottom: 14,
  },
  artPanelMobile: {
    minHeight: 230,
    paddingTop: 25,
    paddingHorizontal: 32,
    paddingBottom: 2,
  },
  panelOrbit: {
    position: 'absolute',
    right: -96,
    bottom: -112,
    width: 275,
    height: 275,
    borderWidth: 48,
    borderColor: COLOR.emphasis_rgba_10,
    borderRadius: 138,
  },
  illustration: {
    width: '100%',
    maxWidth: 310,
    aspectRatio: 1,
    filter: `drop-shadow(0 22px 30px ${NAVY}1f)`,
  },
  illustrationStacked: {
    maxWidth: 340,
  },
  illustrationMobile: {
    maxWidth: 285,
  },
  contentPanel: {
    flex: 1,
    minWidth: 0,
    padding: 66,
    alignItems: 'center',
    justifyContent: 'center',
  },
  contentPanelStacked: {
    flexBasis: 'auto',
    flexGrow: 0,
    flexShrink: 0,
    paddingVertical: 50,
    paddingHorizontal: 64,
  },
  contentPanelMobile: {
    paddingTop: 38,
    paddingHorizontal: 26,
    paddingBottom: 40,
  },
  contentInner: {
    width: '100%',
    maxWidth: 360,
  },
  eyebrow: {
    alignSelf: 'flex-start',
    minHeight: 23,
    marginBottom: 22,
    paddingHorizontal: 11,
    borderRadius: 15,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: COLOR.emphasis_rgba_10,
  },
  eyebrowText: {
    ...FONT.extrabold,
    color: CORAL_DARK,
    fontSize: FONT_SIZE.micro,
    letterSpacing: 1.4,
  },
  title: {
    ...FONT.extrabold,
    maxWidth: 360,
    color: NAVY,
    fontSize: FONT_SIZE.ginormous,
    letterSpacing: -2.2,
    lineHeight: 49,
  },
  titleMobile: {
    fontSize: 45,
    letterSpacing: -2,
    lineHeight: 47,
  },
  message: {
    ...FONT.medium,
    maxWidth: 360,
    marginTop: 22,
    color: MUTED,
    fontSize: FONT_SIZE.small,
    lineHeight: 21.5,
  },
  messageMobile: {
    marginTop: 19,
    fontSize: FONT_SIZE.small,
    lineHeight: 23,
  },
  button: {
    alignSelf: 'flex-start',
    minHeight: 40,
    marginTop: 28,
    paddingHorizontal: 10,
    borderWidth: 2,
    borderColor: CORAL,
    borderRadius: 11,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: CORAL,
    boxShadow: `0 12px 25px ${COLOR.emphasis_rgba_30}`,
  },
  buttonHovered: {
    borderColor: COLOR.redOrangeColor,
    backgroundColor: COLOR.redOrangeColor,
    transform: [{ translateY: -2 }],
  },
  buttonMobile: {
    alignSelf: 'stretch',
  },
  buttonText: {
    ...FONT.extrabold,
    color: NAVY,
    fontSize: FONT_SIZE.tiny,
  },
  buttonArrow: {
    ...FONT.bold,
    marginLeft: 12,
    color: NAVY,
    fontSize: FONT_SIZE.regular,
    lineHeight: 17,
  },
  helpNote: {
    marginTop: 27,
    paddingTop: 21,
    borderTopWidth: 1,
    borderTopColor: COLOR.accent,
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
  },
  infoIcon: {
    width: 14,
    height: 14,
    borderWidth: 1.5,
    borderColor: CORAL_DARK,
    borderRadius: 7,
    alignItems: 'center',
    justifyContent: 'center',
  },
  infoIconText: {
    ...FONT.bold,
    color: CORAL_DARK,
    fontSize: FONT_SIZE.micro,
    lineHeight: 10,
  },
  helpText: {
    ...FONT.semibold,
    flex: 1,
    color: MUTED,
    fontSize: FONT_SIZE.tiny,
    lineHeight: 15,
  },
  logoWrap: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  brandingImage: {
    width: 60,
    height: 60,
  },
  brandingText: {
    ...FONT.medium,
    fontSize: FONT_SIZE.large,
    color: COLOR.textReversed,
    marginLeft: 14,
    letterSpacing: 2,
  },
});
